import { DiscountsType } from 'components/Modals/EditDiscountModal/EditDiscountModal';
import { queryClient } from 'index';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import {
  changeDiscountCartLine,
  changeQuantityCartLine,
  deleteShoppingCartItem,
  duplicateCartLine,
} from 'services/ShoppingCartService';
import { LogyxError, getToastErrorMessage } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';

export const useDuplicateCartLine = () => {
  const { t } = useTranslation();
  return useMutation(
    (params: { cartLineId: number; shoppingCartId: string }) =>
      duplicateCartLine(params.cartLineId, params.shoppingCartId),
    {
      onSuccess: () => {
        toast.success(t('Successfully duplicated order line'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_SHOPPING_CART]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.DUPLICATE_CART_LINE,
    }
  );
};

export const useChangeDiscountCartLine = (
  cartId: string,
  cartLineId: number
) => {
  const { t } = useTranslation();
  return useMutation(
    (params: { discountAmount: string | null; discountType: DiscountsType }) =>
      changeDiscountCartLine(
        cartId,
        cartLineId,
        params.discountAmount,
        params.discountType
      ),
    {
      onSuccess: () => {
        toast.success(t('Successfully changed discount for order line'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_SHOPPING_CART]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.CHANGE_DISCOUNT_CART_LINE,
    }
  );
};

export const useChangeQuantityCartLine = (cartLineId: number) => {
  const { t } = useTranslation();
  return useMutation(
    (quantity: number) => changeQuantityCartLine(cartLineId, quantity),
    {
      onSuccess: () => {
        toast.success(t('Successfully changed quantity for order line'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_SHOPPING_CART]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.CHANGE_QUANTITY_CART_LINE,
    }
  );
};

export const useDeleteItemFromShoppingCart = () => {
  const { t } = useTranslation();
  return useMutation(
    (params: { itemId: number; shoppingCartId: string }) =>
      deleteShoppingCartItem(params.itemId, params.shoppingCartId),
    {
      onSuccess: () => {
        toast.success(t('Item removed from order'), {
          toastId: ReactMutationKeys.DELETE_SHOPPING_CART_ITEM,
        });
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      onSettled: () => {
        queryClient.removeQueries(ReactQueryKeys.GET_SHOPPING_CART);
      },
      mutationKey: ReactMutationKeys.DELETE_SHOPPING_CART_ITEM,
    }
  );
};
